import React from 'react';

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { BulkUploadStatus } from './types';

interface Props {
  open: boolean;
  onClose: () => void;
  upload: BulkUploadStatus | null;
}

export default function UploadResultsDialog({ open, onClose, upload }: Props) {
  const hasResults = upload?.results && upload.results.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Results</Typography>
        <Typography variant="body2" color="text.secondary">
          Upload ID: {upload?.batchId}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {!hasResults ? (
          <Typography color="text.secondary">No results available.</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Row</TableCell>
                  <TableCell>Vendor ID</TableCell>
                  <TableCell>Hotel Code</TableCell>
                  <TableCell>Rate Code</TableCell>
                  <TableCell>Rate Plan Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Warnings</TableCell>
                  <TableCell>Errors</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {upload?.results?.map((result, index) => (
                  <TableRow key={result.row_number ?? index}>
                    <TableCell>{result.row_number}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{result.vendor_id}</TableCell>
                    <TableCell>{result.hotel_code}</TableCell>
                    <TableCell>{result.rate_code}</TableCell>
                    <TableCell>{result.rate_plan_name}</TableCell>
                    <TableCell>
                      <Chip
                        label={result.status}
                        color={result.status === 'success' ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {result.action ? (
                        <Chip
                          label={result.action}
                          color={result.action === 'created' ? 'info' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {result.warnings && result.warnings.length > 0 ? (
                        <Box>
                          {result.warnings.map((msg, idx) => (
                            <Typography
                              key={idx}
                              variant="body2"
                              color="warning.main"
                              display="block"
                              sx={{ fontSize: '0.8rem' }}
                            >
                              • {msg}
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {result.errors && result.errors.length > 0 ? (
                        <Box>
                          {result.errors.map((msg, idx) => (
                            <Typography
                              key={idx}
                              variant="body2"
                              color="error.main"
                              display="block"
                              sx={{ fontSize: '0.8rem' }}
                            >
                              • {msg}
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
